import {
  Box, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, alpha,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { StatWidget } from '../../components/StatWidget';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShowChartIcon from '@mui/icons-material/ShowChart';

const GREEN = '#16c784';
const RED = '#ea3943';
const POLL_MS = 10_000;

interface ClosedPnlRow {
  provider: string;
  accountLabel: string | null;
  symbol: string;
  market: string;
  instrument: string;
  side: string;
  qty: string;
  entryPrice: string;
  exitPrice: string;
  realizedPnl: string;
  openingFee: string;
  closingFee: string;
  fundingFee: string;
  feeCurrency: string;
  tradeType: string;
  closedTime: number;
}

const pnlColor = (n: number) => (n > 0 ? GREEN : n < 0 ? RED : 'text.primary');
const pnlHex = (n: number) => (n > 0 ? GREEN : n < 0 ? RED : '#8b97b0');

/** Trim trailing zeros for display without precision loss. */
const fmtNum = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  if (!s.includes('.')) return s;
  const t = s.replace(/0+$/, '').replace(/\.$/, '');
  return t === '' ? '0' : t;
};

const signed = (n: number) => `${n > 0 ? '+' : ''}${n.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
const fee = (v: string, cur: string) => (Number(v) ? `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 8 })} ${cur}` : '0');

export default function PnlPage() {
  const { data } = useQuery({
    queryKey: ['closed-pnl'],
    queryFn: async () => (await api.get('/trading/pnl')).data.data.items as ClosedPnlRow[],
    refetchInterval: POLL_MS,
  });
  const rows = data ?? [];

  const totalPnl = rows.reduce((s, r) => s + Number(r.realizedPnl || 0), 0);
  const totalFees = rows.reduce((s, r) => s + Number(r.openingFee || 0) + Number(r.closingFee || 0) + Number(r.fundingFee || 0), 0);
  const wins = rows.filter((r) => Number(r.realizedPnl) > 0).length;
  const winRate = rows.length ? (wins / rows.length) * 100 : 0;

  return (
    <Box>
      <PageHeader title="P&L" subtitle="Realized profit & loss on closed positions — synced from your exchanges" />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <StatWidget label="Realized P&L" value={signed(totalPnl)} icon={<TrendingUpIcon />} color={pnlHex(totalPnl)}
          hint={`${rows.length} closed trade${rows.length === 1 ? '' : 's'}`} />
        <StatWidget label="Total Fees" value={totalFees.toLocaleString(undefined, { maximumFractionDigits: 6 })} icon={<ReceiptLongIcon />} color="#f5a623"
          hint="Opening + closing + funding" />
        <StatWidget label="Win Rate" value={`${winRate.toFixed(0)}%`} icon={<ShowChartIcon />} color="#4f8cff"
          hint={`${wins} winning trade${wins === 1 ? '' : 's'}`} />
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 980, '& tbody tr:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Market</TableCell>
                  <TableCell>Instrument</TableCell>
                  <TableCell align="right">Entry Price</TableCell>
                  <TableCell align="right">Traded Price</TableCell>
                  <TableCell align="right">Order Quantity</TableCell>
                  <TableCell>Trade Type</TableCell>
                  <TableCell align="right">Realized P&L</TableCell>
                  <TableCell align="right">Opening Fee</TableCell>
                  <TableCell align="right">Closing Fee</TableCell>
                  <TableCell align="right">Funding Fee</TableCell>
                  <TableCell align="right">Trade Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => {
                  const pnl = Number(r.realizedPnl);
                  return (
                    <TableRow key={`${r.provider}-${r.symbol}-${r.closedTime}-${i}`}>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {r.symbol}
                        <Box component="span" sx={{ ml: 0.75, px: 0.6, py: '1px', borderRadius: '4px', fontSize: 10, fontWeight: 700, color: '#9b8cff', backgroundColor: alpha('#9b8cff', 0.14), border: `1px solid ${alpha('#9b8cff', 0.3)}` }}>
                          {r.market === 'FUTURES' ? 'Perp' : r.market}
                        </Box>
                        <Box component="span" sx={{ ml: 0.5, fontSize: 10, color: 'text.secondary' }}>{r.provider}</Box>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{r.instrument}</TableCell>
                      <TableCell align="right" className="mono">{fmtNum(r.entryPrice)}</TableCell>
                      <TableCell align="right" className="mono">{fmtNum(r.exitPrice)}</TableCell>
                      <TableCell align="right" className="mono" sx={{ color: pnlColor(pnl) }}>{fmtNum(r.qty)}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{r.tradeType}</TableCell>
                      <TableCell align="right" className="mono" sx={{ color: pnlColor(pnl), fontWeight: 700 }}>
                        {signed(pnl)} {r.feeCurrency}
                      </TableCell>
                      <TableCell align="right" className="mono" sx={{ color: 'text.secondary' }}>{fee(r.openingFee, r.feeCurrency)}</TableCell>
                      <TableCell align="right" className="mono" sx={{ color: 'text.secondary' }}>{fee(r.closingFee, r.feeCurrency)}</TableCell>
                      <TableCell align="right" className="mono" sx={{ color: 'text.secondary' }}>{Number(r.fundingFee) ? fee(r.fundingFee, r.feeCurrency) : '0'}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {r.closedTime ? new Date(r.closedTime).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      No closed trades yet. Realized P&L appears here once you close a position.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
